if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.relocated) {
          document.body.appendChild(this);
          this.dataset.relocated = 'true';
        }

        this.sectionId = this.dataset.section;
        this.productInfo = document.querySelector(`product-info[data-section='${this.sectionId}']`);
        this.priceContainer = this.querySelector('[data-sticky-price]');
        this.imageElement = this.querySelector('[data-sticky-image]');
        this.variantInput = this.querySelector('[data-sticky-variant-input]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.initialImageSrc = this.imageElement?.getAttribute('src');
        this.initialImageAlt = this.imageElement?.getAttribute('alt');
        this.defaultAddToCartText = window.variantStrings?.addToCart || this.submitButtonText?.textContent?.trim() || '';
        this.defaultSoldOutText = window.variantStrings?.soldOut || 'Sold out';
        this.defaultUnavailableText = window.variantStrings?.unavailable || 'Unavailable';

        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.evaluateVisibility = this.evaluateVisibility.bind(this);

        this.toggleBar(false);
        this.initObserver();
        this.subscribeToEvents();
        this.syncInitialState();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.disableScrollListener();
        this.variantUnsubscriber?.();
        if (this.productInfo && this.productInfoListener) {
          this.productInfo.removeEventListener('product-info:loaded', this.productInfoListener);
        }
      }

      toggleBar(show) {
        this.classList.toggle('is-visible', show);
        this.hidden = !show;
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      initObserver() {
        this.targetButton = this.productInfo?.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!this.targetButton) {
          this.toggleBar(true);
          return;
        }

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
            rootMargin: '0px',
            threshold: [0, 1],
          });
          this.intersectionObserver.observe(this.targetButton);
        }

        this.enableScrollListener();
        this.evaluateVisibility();
      }

      enableScrollListener() {
        if (this.scrollHandler) return;
        this.scrollHandler = () => window.requestAnimationFrame(this.evaluateVisibility);
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        window.addEventListener('resize', this.scrollHandler);
      }

      disableScrollListener() {
        if (!this.scrollHandler) return;
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.scrollHandler);
        this.scrollHandler = null;
      }

      handleIntersection() {
        this.evaluateVisibility();
      }

      evaluateVisibility() {
        if (!this.productInfo) {
          this.toggleBar(true);
          return;
        }

        if (!this.targetButton || !document.body.contains(this.targetButton)) {
          this.targetButton = this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          if (this.targetButton && this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver.observe(this.targetButton);
          }
          if (!this.targetButton) {
            this.toggleBar(true);
            return;
          }
        }

        const rect = this.targetButton.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const isOnScreen = rect.bottom >= 0 && rect.top <= viewportHeight;
        this.toggleBar(!isOnScreen);
      }

      subscribeToEvents() {
        if (typeof subscribe === 'function') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }

        if (this.productInfo) {
          this.productInfoListener = () => {
            this.initObserver();
            this.syncInitialState();
          };
          this.productInfo.addEventListener('product-info:loaded', this.productInfoListener, { once: true });
        }
      }

      syncInitialState() {
        const variant = this.getCurrentVariant();
        this.updateFromVariant(variant);
        this.updatePrice();
        this.evaluateVisibility();
      }

      getCurrentVariant() {
        if (!this.productInfo) return null;
        const selectedVariantNode = this.productInfo.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantNode || !selectedVariantNode.innerHTML.trim()) return null;
        try {
          return JSON.parse(selectedVariantNode.innerHTML);
        } catch (error) {
          console.error('Sticky add-to-cart: failed to parse selected variant', error);
          return null;
        }
      }

      handleVariantChange(event) {
        const { data } = event || {};
        if (!data || data.sectionId !== this.sectionId) return;
        this.updateFromVariant(data.variant, data.html);
        this.evaluateVisibility();
      }

      updateFromVariant(variant, html) {
        this.updateVariantInput(variant);
        this.updateButtonState(variant);
        this.updatePrice(html);
        this.updateImage(variant);
      }

      updateVariantInput(variant) {
        if (!this.variantInput) return;
        const available = variant?.available;
        this.variantInput.value = variant?.id ?? '';
        if (!available) {
          this.variantInput.setAttribute('disabled', 'disabled');
        } else {
          this.variantInput.removeAttribute('disabled');
        }
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateButtonState(variant) {
        if (!this.submitButton || !this.submitButtonText) return;
        if (!variant) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = this.defaultUnavailableText;
          return;
        }

        if (!variant.available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = this.defaultSoldOutText;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings?.addToCart || this.defaultAddToCartText;
        }
      }

      updatePrice(html) {
        if (!this.priceContainer) return;
        let priceSource = null;
        if (html) {
          priceSource = html.getElementById(`price-${this.sectionId}`);
        }
        if (!priceSource && this.productInfo) {
          priceSource = this.productInfo.querySelector(`#price-${this.sectionId}`);
        }
        if (priceSource) {
          this.priceContainer.innerHTML = priceSource.innerHTML;
        }
      }

      updateImage(variant) {
        if (!this.imageElement || this.imageElement.tagName !== 'IMG') return;
        const preview = variant?.featured_media?.preview_image || variant?.featured_media;
        if (preview?.src) {
          const sizedSrc = this.getSizedImage(preview.src, 160);
          if (sizedSrc) this.imageElement.src = sizedSrc;
          this.imageElement.alt = preview.alt || this.initialImageAlt || this.imageElement.alt;
        } else if (this.initialImageSrc) {
          this.imageElement.src = this.initialImageSrc;
          if (this.initialImageAlt) {
            this.imageElement.alt = this.initialImageAlt;
          }
        }
      }

      getSizedImage(src, size) {
        if (!src) return null;
        if (window.Shopify?.Image && typeof Shopify.Image.getSizedImageUrl === 'function') {
          return Shopify.Image.getSizedImageUrl(src, `${size}x${size}`);
        }
        return src;
      }
    }
  );
}
